import { motion, AnimatePresence } from "framer-motion";
import { X, ExternalLink, Calendar, MapPin, Tag, ArrowRight } from "lucide-react";
import { imgSrc } from "@/lib/api";
import { scrollToSection } from "@/lib/smoothScroll";
import { useLang } from "@/context/LanguageContext";

function VideoEmbed({ url }) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) {
    return (
      <div className="mt-5 overflow-hidden rounded-xl border border-slate-800 aspect-video">
        <iframe src={`https://www.youtube.com/embed/${yt[1]}`} title="Video progetto" className="h-full w-full" allowFullScreen />
      </div>
    );
  }
  return (
    <video controls preload="metadata" src={imgSrc(url)} className="mt-5 w-full rounded-xl border border-slate-800" />
  );
}

export default function PartnerModal({ item, onClose }) {
  const { t } = useLang();
  const logo = item?.logo_url ? imgSrc(item.logo_url) : null;
  const cover = item?.cover ? imgSrc(item.cover, 960) : null;
  const photos = item?.photos || [];
  const info = item?.info || {};
  const infoChips = [
    [info.year, Calendar],
    [info.location, MapPin],
    [info.type, Tag],
  ].filter(([v]) => v);

  const goContact = () => {
    onClose();
    setTimeout(() => scrollToSection("#contatti"), 250);
  };

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[85] flex items-center justify-center bg-[#090D14]/92 backdrop-blur-md p-4"
        >
          <motion.div
            data-testid="partner-modal"
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-xl max-h-[88vh] overflow-y-auto rounded-2xl border border-slate-700 bg-[#0C1220] shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
          >
            <button onClick={onClose} aria-label={t("modal.close")} data-testid="partner-modal-close" className="absolute top-4 right-4 z-10 rounded-full border border-slate-700 bg-[#090D14]/70 p-2 text-slate-300 hover:text-amber-400 hover:border-amber-400/60 transition-colors">
              <X className="h-4 w-4" />
            </button>

            {cover && (
              <div className="relative h-44 sm:h-56 overflow-hidden rounded-t-2xl">
                <img src={cover} alt={item.name} className="h-full w-full object-cover" />
                <span className="absolute inset-0 bg-gradient-to-t from-[#0C1220] to-transparent" />
              </div>
            )}

            <div className="p-6 sm:p-8">
              <div className="flex items-center gap-4">
                {logo ? (
                  <img src={logo} alt={`Logo ${item.name}`} className="h-14 w-auto min-w-14 max-w-48 shrink-0 rounded-xl bg-white object-contain px-2.5 py-2 shadow-lg" />
                ) : (
                  <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-slate-700 bg-slate-900 font-display text-2xl text-amber-400">
                    {item.name.slice(0, 2).toUpperCase()}
                  </span>
                )}
                <div className="min-w-0">
                  <h3 className="font-display text-2xl sm:text-3xl text-slate-50 leading-tight">{item.name}</h3>
                  {item.title && <p className="text-sm font-semibold text-sky-300 leading-snug">{item.title}</p>}
                  {item.tagline && <p className="mt-0.5 text-[10px] uppercase tracking-[0.22em] text-amber-400/90">{item.tagline}</p>}
                </div>
              </div>

              {infoChips.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {infoChips.map(([v, Icon]) => (
                    <span key={v} className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1 text-[10px] uppercase tracking-[0.15em] text-slate-300">
                      <Icon className="h-3 w-3 text-amber-400/80" /> {v}
                    </span>
                  ))}
                </div>
              )}

              {(item.intro || item.description) && (
                <p className="mt-5 text-sm text-slate-300 leading-relaxed">{item.intro || item.description}</p>
              )}

              {item.project && (
                <div className="mt-6">
                  <h4 className="text-[10px] font-bold uppercase tracking-[0.25em] text-sky-400">{t("modal.project")}</h4>
                  <p className="mt-2 text-sm text-slate-300 leading-relaxed">{item.project}</p>
                </div>
              )}

              {item.experience && (
                <div className="mt-6">
                  <h4 className="text-[10px] font-bold uppercase tracking-[0.25em] text-sky-400">{t("modal.experience")}</h4>
                  <p className="mt-2 text-sm text-slate-300 leading-relaxed">{item.experience}</p>
                </div>
              )}

              <VideoEmbed url={item.video_url} />

              {photos.length > 0 && (
                <div className="mt-5 grid grid-cols-3 gap-2" data-testid="partner-modal-photos">
                  {photos.map((p, i) => (
                    <img key={p + i} src={imgSrc(p, 400)} alt={`${item.name} ${i + 1}`} loading="lazy" className="aspect-square w-full rounded-lg border border-slate-800 object-cover" />
                  ))}
                </div>
              )}

              <div className="mt-7 flex flex-wrap items-center gap-3 border-t border-slate-800 pt-5">
                <button
                  data-testid="partner-modal-cta"
                  onClick={goContact}
                  className="group inline-flex items-center gap-2 rounded-full bg-amber-400 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-[#090D14] transition-transform duration-300 hover:scale-[1.03]"
                >
                  {t("modal.cta")}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                {item.link && (
                  <a href={item.link} target="_blank" rel="noreferrer" data-testid="partner-modal-link" className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-300 hover:border-sky-400/60 hover:text-sky-300 transition-colors">
                    {t("modal.visit")}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
